import { useReduxState } from 'hooks';
import { Icon } from 'components/common/icons';

interface Props extends React.HTMLAttributes<HTMLElement> {
  checked: boolean;
  darkMode?: boolean;
  disabled?: boolean;
  id: string;
  label: React.ReactNode;
  labelAdditionalContent?: React.ReactNode;
  labelStyle?: React.CSSProperties;
  onChange?: (value: any) => void;
  stopPropagation?: boolean;
  style?: React.CSSProperties;
  styleAsCard?: boolean;
  tooltip?: string;
  tooltipHideIcon?: boolean;
  value: any;
}

const RadioInput = ({
  checked,
  darkMode,
  disabled,
  id,
  label,
  labelAdditionalContent,
  labelStyle,
  onChange,
  stopPropagation = true,
  style,
  styleAsCard,
  tooltip,
  tooltipHideIcon,
  value
}: Props) => {
  const { theme } = useReduxState();

  let color = 'rgba(0, 0, 0, 0.1)';
  let iconColor = theme.successTextColor;
  let activeColor = theme.successColor;
  let outlineColor = 'rgba(0, 0, 0, 0.15)';
  let labelColor = theme.textColorLight;
  if (darkMode) {
    color = 'rgba(255, 255, 255, 0.5)';
    iconColor = theme.successTextColorDark;
    activeColor = theme.successColorDark;
    outlineColor = 'rgba(255, 255, 255, 0.2)';
    labelColor = '#fff';
  }

  const handleChange = (ev) => onChange(ev.target.value);

  const handleClick = (ev) => ev.stopPropagation();

  return (
    <div
      className="radio"
      style={style}
      {...(stopPropagation ? { onClick: handleClick } : {})}
    >
      <input
        checked={checked}
        className="radio-input"
        disabled={disabled}
        id={id}
        onChange={handleChange}
        type="radio"
        value={value}
      />
      <div className={`label ${styleAsCard ? 'label-card' : ''}`}>
        <label
          htmlFor={id}
          style={labelStyle}
          {...(tooltip
            ? {
                'aria-label': tooltip,
                'data-tooltip': tooltip
              }
            : {})}
        >
          {label}
          {tooltip && !tooltipHideIcon ? (
            <Icon
              icon="question-circle"
              style={{ fontSize: '1.4rem', marginLeft: 5 }}
            />
          ) : null}
        </label>

        {labelAdditionalContent ? (
          <div className="additional-content">{labelAdditionalContent}</div>
        ) : null}
      </div>

      <style jsx>{`
        .radio {
          position: relative;
        }
        .radio-input {
          opacity: 0;
          overflow: hidden;
          position: absolute;
          height: 0.1px;
          width: 0.1px;
          z-index: -1;
        }
        .radio-input + .label label {
          color: ${labelColor};
          cursor: pointer;
          display: inline-block;
          font-size: 1.5rem;
          font-weight: 400;
          padding-left: 24px;
          position: relative;
          white-space: normal;
          width: auto;
          word-wrap: break-word;
        }
        .radio-input + .label label::before {
          background-color: ${color};
          border: 0;
          border-radius: 10px;
          box-shadow: none;
          content: '';
          display: inline-block;
          left: 0;
          height: 18px;
          padding: 0;
          position: absolute;
          top: 1px;
          width: 18px;
          z-index: 1;
        }
        .radio-input + .label label::after {
          background-color: transparent;
          border-radius: 5px;
          content: '';
          display: inline-block;
          height: 6px;
          left: 6px;
          position: absolute;
          top: 7px;
          transition: background-color 0.1s;
          width: 6px;
          z-index: 1;
        }
        .radio-input + .label label:hover::after {
          background-color: rgba(0, 0, 0, 0.2);
        }
        .radio-input:checked + .label label::before {
          background-color: ${activeColor};
        }
        .radio-input:checked + .label label::after {
          background-color: ${iconColor};
        }
        .radio-input:disabled + .label label {
          cursor: default;
          opacity: 0.5;
        }
        .radio-input:disabled + .label label:hover::before {
          color: ${color};
        }
        .radio-input:disabled + .label label:hover::after {
          background-color: transparent;
        }
        .radio-input:disabled:checked + .label label:hover::before {
          color: ${activeColor};
        }
        .radio-input:disabled:checked + .label label:hover::after {
          background-color: ${iconColor};
        }
        .radio-input:focus + .label label::before,
        .radio-input:focus:checked + .label label::before {
          box-shadow: 0 0 0 5px ${outlineColor};
        }
        .radio-input + .label-card label {
          border: 1px solid ${theme.lightGreyDark};
          border-radius: 7px;
          display: block;
          padding: 10px 40px 10px 10px;
        }
        .radio-input:checked + .label-card label {
          border-color: #a1a8aa;
        }
        .radio-input + .label-card label::before {
          top: calc(50% - 9px);
          left: auto;
          right: 16px;
        }
        .radio-input + .label-card label::after {
          top: calc(50% - 3px);
          left: auto;
          right: 22px;
        }
        .additional-content {
          font-weight: 400;
          padding-left: 24px;
          margin: 5px 0;
        }
      `}</style>
    </div>
  );
};

export default RadioInput;
